import {Component, AfterViewInit, Inject, ViewChild} from '@angular/core';
import {FlexGrid, DataMap} from '@grapecity/wijmo.grid';
import {
    CollectionView, SortDescription
} from '@grapecity/wijmo';
import {NewsMlService} from './news-ml.service';
import {DATEFORMAT_CULTURE} from '../shared/tokens';
import {NewsItem} from '../shared/domain/news-item';
import {NewsItemHelpers} from '../shared/helpers/news-item.helpers';
import * as vkbeautify from 'vkbeautify';
import {ItemLanguageElement} from '../shared/domain/item-language-element';
import {PoaTranslatedString} from '../shared/domain/poa-translated-string';
import {PoaLanguage} from '../shared/domain/poa-language';
import {interval, Subscription} from 'rxjs';
import {WindowRef} from '../shared/window-ref.service';

function nullToUndefined<T>(item: T | undefined | null): T | undefined {
    return item === null ? undefined : item;
}

@Component({
    selector: 'sim-news-ml',
    templateUrl: './news-ml.component.html',
})

export class NewsMlComponent implements AfterViewInit {

    private readonly INTERVALL_AUTOMATIC_REFRESH_MS;

    readonly newsItemsCollectionView = new CollectionView<NewsItem>();

    @ViewChild('detailTextGrid')
    detailTextGrid?: FlexGrid;

    selectedNewsItemStringTable: ItemLanguageElement[];
    selectedNewsItemLanguages: PoaLanguage[];

    useEndpointRealMessage = true;

    refreshPending = true;

    automaticRefresh = false;

    newsMlPrettyPrint: string | null = null;

    readonly urgencyTypeDataMap = NewsMlComponent.initUrgencyTypeDataMap();

    private pendingRefreshGUI: ReturnType<typeof setTimeout> | null = null;

    private refreshSubscription?: Subscription;

    static initUrgencyTypeDataMap(): DataMap {
        const urgencyTypes: {}[] = [];

        urgencyTypes.push({key: 0, text: 'Unknown'});
        urgencyTypes.push({key: 1, text: 'Test'});
        urgencyTypes.push({key: 2, text: 'Exercise'});
        urgencyTypes.push({key: 3, text: 'Information'});
        urgencyTypes.push({key: 4, text: 'Warning'});
        urgencyTypes.push({key: 5, text: 'Alarm'});

        return new DataMap(urgencyTypes, 'key', 'text');
    }

    constructor(public service: NewsMlService,
                @Inject(DATEFORMAT_CULTURE) private readonly dateCulture: string,
                private readonly winRef: WindowRef) {
        this.newsItemsCollectionView.sortDescriptions.push(new SortDescription('contentCreated', false));
        this.selectedNewsItemStringTable = new Array<ItemLanguageElement>();
        this.selectedNewsItemLanguages = new Array<PoaLanguage>();

        this.INTERVALL_AUTOMATIC_REFRESH_MS = Number(this.winRef.getGlobalVarValue('INTERVALL_AUTOMATIC_REFRESH_MS')) || 10000;
    }

    ngAfterViewInit(): void {
        this.getNewsMLMessages(true);
    }

    endpointChanged(): void {
        this.selectedNewsItemStringTable.length = 0;
        this.getNewsMLMessages();
    }

    manualRefresh(): void {
        this.getNewsMLMessages();
    }

    toggleAutomaticRefresh(): void {
        this.automaticRefresh = !this.automaticRefresh;

        if (this.automaticRefresh === true) {
            console.log('Start automatic refresh', this.INTERVALL_AUTOMATIC_REFRESH_MS);
            const source = interval(this.INTERVALL_AUTOMATIC_REFRESH_MS);

            this.refreshSubscription = source.subscribe(() => {
                this.getNewsMLMessages();
            });
        } else {
            console.log('Stop automatic refresh');
            if (this.refreshSubscription) {
                this.refreshSubscription.unsubscribe();
            }
        }
    }

    onMeldungSelectionChanged(event: MouseEvent): void {
        this.selectedNewsItemStringTable.length = 0;
        if (this.selectedNewsItem) {
            this.buildDataGrid(this.selectedNewsItem);
            if (this.detailTextGrid) {
                this.detailTextGrid.collectionView.refresh();
            } else {
                // On first selection, detailTextGrid is not valid yet
                setTimeout(() => {
                    this.refreshGUI();
                }, 100);
            }
        }
    }

    private getNewsMLMessages(initial: boolean = false): void {
        console.log('getNewsMLMessages()');
        this.refreshPending = true;
        let newsItems: NewsItem[];
        this.service
            .getNewsMLMessage(this.useEndpointRealMessage)
            .subscribe(
                (newsMlMessage: string) => {
                    this.newsMlPrettyPrint = vkbeautify.xml(newsMlMessage);
                    newsItems = NewsItemHelpers.getMessages(newsMlMessage);
                    // console.log('newsItems', newsItems);
                    if (initial) {
                        this.newsItemsCollectionView.sourceCollection = newsItems;
                        this.resetSelection();
                    } else {
                        this.newsItemsCollectionView.beginUpdate();
                        this.updateNewsItemCollectionView(newsItems);
                        this.newsItemsCollectionView.endUpdate();
                    }
                    this.refreshPending = false;
                },
                error => {
                    this.newsMlPrettyPrint = null;
                    this.refreshPending = false;
                    console.log(`Get newsMl message failed: ${error}`);
                }
            );
    }

    private updateNewsItemCollectionView(receivedNewsItems: NewsItem[]): void {
        // Add newsItems not present in collectionView
        receivedNewsItems.forEach((newsItem: NewsItem) => {
            const messageInView = this.newsItemsCollectionView.items.find(
                (item: NewsItem) => item.guid === newsItem.guid);

            if (messageInView === undefined) {
                console.log('Insert ', newsItem.altId);
                this.newsItemsCollectionView.sourceCollection.push(newsItem);
            }
        });

        // Remove newsItems from collectionView if not present in receivedNewsItems
        this.newsItemsCollectionView.items.forEach(message => {
            const item = receivedNewsItems.filter((newsItem: NewsItem) => newsItem.guid === message.guid);
            if (item.length === 0) {
                console.log('Remove ', message.altId);
                if (receivedNewsItems.length === 0) {
                    this.selectedNewsItemStringTable.length = 0;
                }
                if (this.newsItemsCollectionView.currentItem === message) {
                    this.resetSelection();
                    this.selectedNewsItemStringTable.length = 0;
                }
                this.newsItemsCollectionView.remove(message);
            }
        });
    }

    private resetSelection(): void {
        this.detailTextGrid?.select(-1, -1);
    }

    public get selectedNewsItem(): NewsItem | undefined {
        if (this.newsItemsCollectionView === undefined) {
            return undefined;
        } else {
            return nullToUndefined(this.newsItemsCollectionView.currentItem);
        }
    }

    private buildDataGrid(newsItem: NewsItem): void {
        this.selectedNewsItemLanguages = newsItem.languages;

        const languages: PoaTranslatedString = {
            de: 'de', fr: 'fr', it: 'it', en: 'en'
        };

        this.insertMultiLangElement('Language', languages);
        this.insertMultiLangElement('Provider', newsItem.provider);
        this.insertSingleLangElement('VersionCreated', this.formatDateString(newsItem.versionCreated));
        this.insertSingleLangElement('ContentCreated', this.formatDateString(newsItem.contentCreated));
        if (newsItem.onset) {
            this.insertSingleLangElement('OnSet', this.formatDateString(newsItem.onset));
        }
        this.insertSingleLangElement('Broader', newsItem.broader);
        this.insertMultiLangElement('Creator', newsItem.creator);
        this.insertMultiLangElement('Headline', newsItem.headline);
        this.insertMultiLangElement('EventType', newsItem.eventType);
        if (newsItem.readoutLanguages) {
            this.insertSingleLangElement('ReadoutLanguages', newsItem.readoutLanguages);
        }
        if (newsItem.dutyToPublish) {
            this.insertSingleLangElement('DutyToPublish', 'True');
            this.insertMultiLangElement('ICARO Message', newsItem.message);
        } else {
            this.insertMultiLangElement('Message', newsItem.message);
        }
    }

    private formatDateString(date: Date): string {
        return date.toLocaleDateString(this.dateCulture) + ' ' +
            date.toLocaleTimeString(this.dateCulture);
    }

    private insertSingleLangElement(feature: string, entry: string): void {
        const data: PoaTranslatedString = {};
        data.de = entry;
        data.fr = entry;
        data.it = entry;
        data.en = entry;
        this.insertMultiLangElement(feature, data);
    }

    private insertMultiLangElement(feature: string, entries: PoaTranslatedString): void {
        this.selectedNewsItemStringTable.push(
            {
                feature,
                entries
            }
        );
    }

    private refreshGUI(): void {
        if (this.detailTextGrid) {
            this.detailTextGrid.rows.forEach((rowItem) => {
                rowItem.allowMerging = true;
            });

            this.detailTextGrid.autoSizeRows();
        }
    }

    public onLoadedRows(): void {
        if (this.pendingRefreshGUI) {
            clearTimeout(this.pendingRefreshGUI);
        }

        // throttle refreshGUI as onLoadedRows is called several times on first load
        this.pendingRefreshGUI = setTimeout(() => {
            this.refreshGUI();
        }, 400);
    }
}
